import Alpine from 'alpinejs';
import axios from '../../config/axios.js';

export function createInvitationThemeBuilder() {
  return {
    templateId: '',
    apiBase: '',
    templateName: '',
    themeVersion: '',
    viewKey: '',
    loading: true,
    saving: false,
    uploading: false,
    previewUrl: '',
    previewKey: Date.now(),
    previewDevice: 'desktop',
    dragIndex: null,
    sectionEditor: {},
    selectedSectionId: null,
    componentVariants: {},
    sectionCatalog: [],
    layoutConfig: { type: 'single_page', navigation: 'scroll', hero_type: 'cover', section_transition: 'fade' },
    designTokens: {
      spacing: { section: '96px', container: '1200px' },
      border_radius: { card: '20px', button: '999px' },
    },
    animationConfig: { page_load: 'fade_up', scroll: 'reveal', gallery: 'zoom' },
    navigationOptions: {},
    heroTypes: {},
    sectionTransitions: {},
    pageLoadAnimations: {},
    galleryAnimations: {},
    themeTypes: {},
    themeType: 'free',
    parentTemplateId: null,
    componentPalette: [],
    componentSchemas: {},
    themeAssets: [],
    assetsLoading: false,
    duplicatingTheme: false,
    bumpingVersion: false,
    componentPreviewLoading: false,
    componentPreviewUrl: '',
    uploadingAsset: false,
    assetUpload: { type: 'image' },
    musicModes: {},
    addSectionKey: '',
    theme: {
      colors: { accent: '#9a6a53', ink: '', paper: '', muted: '', card: '', surface: '' },
      fonts: { body: 'Plus Jakarta Sans', heading: 'Cormorant Garamond', script: 'Great Vibes' },
      background: { type: 'gradient', cover_image: null, cover_overlay: 'rgba(0,0,0,.45)' },
      couple: { layout: 'stacked', photo_shape: 'circle', photo_size: 'md', show_ampersand: true },
      cover: { style: 'fullscreen' },
      effects: { depth_3d: false, parallax: false },
      music: { mode: 'invitation', url: '', autoplay: false },
    },
    formSchema: [],
    formSectionsMeta: [],
    displaySections: [],
    fontPresets: [],
    coupleLayouts: {},
    photoSizes: ['sm', 'md', 'lg'],
    coverImageUrl: '',
    themeVersions: [],
    versionsLoading: false,
    restoringVersion: false,
    colorFields: [
      { key: 'accent', label: 'Accent / Primary', placeholder: '#9a6a53' },
      { key: 'ink', label: 'Teks utama', placeholder: 'auto' },
      { key: 'paper', label: 'Background halaman', placeholder: 'auto' },
      { key: 'muted', label: 'Teks sekunder', placeholder: 'auto' },
      { key: 'card', label: 'Kartu / panel', placeholder: 'auto' },
      { key: 'surface', label: 'Surface', placeholder: 'auto' },
    ],

    init() {
      this.templateId = this.$el.getAttribute('data-template-id') || '';
      this.apiBase = this.$el.getAttribute('data-api-base') || '/api/v1/kasir/engagement/invitation-templates';
      if (this.templateId) {
        this.loadTheme();
      }
    },

    get selectedSection() {
      return this.displaySections.find((s) => s.id === this.selectedSectionId) || null;
    },

    get selectedSectionFields() {
      const key = this.selectedSection?.key;
      if (!key || !this.sectionEditor[key]) return [];
      return this.sectionEditor[key].fields || [];
    },

    get variantsForSelectedSection() {
      const key = this.selectedSection?.key;
      if (!key) return [];
      return this.componentVariants[key] || [{ key, label: this.selectedSection?.label || key }];
    },

    get previewSrc() {
      const base = this.componentPreviewUrl || this.previewUrl;
      if (!base) return 'about:blank';
      const sep = base.includes('?') ? '&' : '?';
      return `${base}${sep}t=${this.previewKey}`;
    },

    async loadTheme() {
      this.loading = true;
      try {
        const { data } = await axios.get(`${this.apiBase}/${this.templateId}/theme`);
        const payload = data.data || {};
        this.templateName = payload.template_name || '';
        this.viewKey = payload.view_key || 'classic';
        this.themeVersion = payload.theme_version || '';
        this.previewUrl = payload.preview_url || '';
        this.sectionEditor = payload.section_editor || {};
        this.componentVariants = payload.component_variants || {};
        this.sectionCatalog = payload.section_catalog || [];
        this.componentPalette = payload.component_palette || [];
        this.componentSchemas = payload.component_schemas || {};
        this.themeType = payload.theme_type || 'free';
        this.parentTemplateId = payload.parent_template_id || null;
        this.layoutConfig = {
          type: 'single_page',
          navigation: 'scroll',
          hero_type: 'cover',
          section_transition: 'fade',
          ...(payload.layout_config || {}),
        };
        this.designTokens = {
          spacing: { section: '96px', container: '1200px' },
          border_radius: { card: '20px', button: '999px' },
          ...(payload.design_tokens || {}),
        };
        if (!this.designTokens.spacing) this.designTokens.spacing = { section: '96px', container: '1200px' };
        if (!this.designTokens.border_radius) this.designTokens.border_radius = { card: '20px', button: '999px' };
        this.animationConfig = payload.animation_config || this.animationConfig;
        this.navigationOptions = payload.defaults?.navigation_options || {};
        this.heroTypes = payload.defaults?.hero_types || {};
        this.sectionTransitions = payload.defaults?.section_transitions || {};
        this.pageLoadAnimations = payload.defaults?.page_load_animations || {};
        this.galleryAnimations = payload.defaults?.gallery_animations || {};
        this.themeTypes = payload.defaults?.theme_types || {};
        this.musicModes = payload.defaults?.music_modes || {};
        this.displaySections = this.normalizeDisplaySections(payload.display_sections || []);
        this.fontPresets = payload.defaults?.font_presets || [];
        this.coupleLayouts = payload.defaults?.couple_layouts || {};
        this.photoSizes = payload.defaults?.photo_sizes || ['sm', 'md', 'lg'];

        if (payload.theme_config) {
          this.theme = this.mergeTheme(payload.theme_config);
        }

        this.coverImageUrl = payload.theme_config?.background?.cover_image_url || '';
        await this.loadFormSectionsMeta();
        await this.loadVersions();
        await this.loadThemeAssets();
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal memuat tema.', 'error');
      } finally {
        this.loading = false;
      }
    },

    async loadFormSectionsMeta() {
      try {
        const formSectionsPath = this.apiBase.includes('/creator/')
          ? '/api/v1/creator/invitation-templates/form-sections'
          : '/api/v1/kasir/engagement/invitation-templates/form-sections';
        const { data } = await axios.get(formSectionsPath);
        this.formSectionsMeta = data.data || [];
      } catch {
        this.formSectionsMeta = [];
      }
    },

    normalizeDisplaySections(sections) {
      return sections.map((sec, index) => ({
        id: sec.id || crypto.randomUUID?.() || `sec-${Date.now()}-${index}`,
        key: sec.key || sec.section_type,
        component_key: sec.component_key || sec.key || sec.section_type,
        label: sec.label || sec.key || sec.section_type,
        enabled: sec.enabled ?? sec.is_enabled ?? true,
        sort_order: sec.sort_order ?? index,
        configuration: sec.configuration || {},
      })).sort((a, b) => a.sort_order - b.sort_order);
    },

    mergeTheme(incoming) {
      return {
        colors: { ...this.theme.colors, ...(incoming.colors || {}) },
        fonts: { ...this.theme.fonts, ...(incoming.fonts || {}) },
        background: { ...this.theme.background, ...(incoming.background || {}) },
        couple: { ...this.theme.couple, ...(incoming.couple || {}) },
        cover: { ...this.theme.cover, ...(incoming.cover || {}) },
        effects: { ...this.theme.effects, ...(incoming.effects || {}) },
        music: { ...this.theme.music, ...(incoming.music || {}) },
      };
    },

    applyFontPreset(id) {
      if (!id) return;
      const preset = this.fontPresets.find((p) => p.id === id);
      if (!preset) return;
      this.theme.fonts.body = preset.body;
      this.theme.fonts.heading = preset.heading;
      this.theme.fonts.script = preset.script;
    },

    selectSection(id) {
      this.selectedSectionId = id;
      const sec = this.displaySections.find((s) => s.id === id);
      if (sec && !sec.configuration) {
        sec.configuration = {};
      }
    },

    setComponentKey(componentKey) {
      if (!this.selectedSectionId || !componentKey) return;
      this.displaySections = this.displaySections.map((sec) => (
        sec.id === this.selectedSectionId ? { ...sec, component_key: componentKey } : sec
      ));
    },

    duplicateSection(id) {
      const source = this.displaySections.find((s) => s.id === id);
      if (!source) return;
      const copy = {
        ...JSON.parse(JSON.stringify(source)),
        id: crypto.randomUUID?.() || `sec-${Date.now()}`,
        sort_order: source.sort_order + 1,
      };
      const list = [...this.displaySections];
      const idx = list.findIndex((s) => s.id === id);
      list.splice(idx + 1, 0, copy);
      this.displaySections = list;
      this.reindexSections();
    },

    removeSection(id) {
      if (this.displaySections.length <= 1) {
        window.toast?.('Minimal satu section harus tersisa.', 'error');
        return;
      }
      this.displaySections = this.displaySections.filter((s) => s.id !== id);
      if (this.selectedSectionId === id) {
        this.selectedSectionId = null;
      }
      this.reindexSections();
    },

    addFromPalette(item) {
      if (!item?.section_type) return;
      const key = item.section_type;
      const meta = this.sectionCatalog.find((s) => s.key === key) || { key, label: item.label || key };
      this.displaySections.push({
        id: crypto.randomUUID?.() || `sec-${Date.now()}`,
        key,
        component_key: item.key,
        label: meta.label || item.label || key,
        enabled: true,
        sort_order: this.displaySections.length,
        configuration: {},
      });
      this.reindexSections();
      this.selectSection(this.displaySections[this.displaySections.length - 1].id);
    },

    async loadThemeAssets() {
      if (!this.templateId) return;
      this.assetsLoading = true;
      try {
        const { data } = await axios.get(`${this.apiBase}/${this.templateId}/theme-assets`);
        this.themeAssets = data.data || [];
      } catch {
        this.themeAssets = [];
      } finally {
        this.assetsLoading = false;
      }
    },

    async uploadThemeAsset(event) {
      const file = event.target.files?.[0];
      if (!file) return;
      this.uploadingAsset = true;
      const form = new FormData();
      form.append('file', file);
      form.append('type', this.assetUpload.type || 'image');
      form.append('label', file.name);
      try {
        const { data } = await axios.post(`${this.apiBase}/${this.templateId}/theme-assets`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (data.data) this.themeAssets.unshift(data.data);
        window.toast?.(data.message || 'Asset diunggah.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal upload asset.', 'error');
      } finally {
        this.uploadingAsset = false;
        event.target.value = '';
      }
    },

    async deleteThemeAsset(assetId) {
      const ok = await window.confirmDialog?.({
        title: 'Hapus asset?',
        message: 'Asset tema akan dihapus permanen.',
        confirmText: 'Hapus',
        variant: 'danger',
      });
      if (!ok) return;
      try {
        await axios.delete(`${this.apiBase}/${this.templateId}/theme-assets/${assetId}`);
        this.themeAssets = this.themeAssets.filter((a) => a.id !== assetId);
        window.toast?.('Asset dihapus.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal menghapus asset.', 'error');
      }
    },

    addSection() {
      const key = this.addSectionKey;
      if (!key) return;
      const meta = this.sectionCatalog.find((s) => s.key === key) || { key, label: key };
      const variants = this.componentVariants[key] || [{ key, label: meta.label }];
      this.displaySections.push({
        id: crypto.randomUUID?.() || `sec-${Date.now()}`,
        key,
        component_key: variants[0]?.key || key,
        label: meta.label || key,
        enabled: true,
        sort_order: this.displaySections.length,
        configuration: {},
      });
      this.addSectionKey = '';
      this.reindexSections();
    },

    sectionConfigValue(field) {
      const cfg = this.selectedSection?.configuration || {};
      if (cfg[field.key] !== undefined && cfg[field.key] !== null && cfg[field.key] !== '') {
        return cfg[field.key];
      }
      return field.default ?? '';
    },

    setSectionConfig(field, value) {
      const id = this.selectedSectionId;
      if (!id) return;
      this.displaySections = this.displaySections.map((sec) => {
        if (sec.id !== id) return sec;
        return {
          ...sec,
          configuration: { ...(sec.configuration || {}), [field.key]: value },
        };
      });
    },

    toggleFormSection(key, checked) {
      if (checked) {
        if (!this.formSchema.includes(key)) {
          this.formSchema.push(key);
        }
      } else {
        this.formSchema = this.formSchema.filter((k) => k !== key);
      }
    },

    toggleDisplaySection(id, checked) {
      this.displaySections = this.displaySections.map((sec) => (
        sec.id === id ? { ...sec, enabled: checked } : sec
      ));
    },

    reindexSections() {
      this.displaySections = this.displaySections.map((sec, index) => ({
        ...sec,
        sort_order: index,
      }));
    },

    moveSection(index, direction) {
      const target = index + direction;
      if (target < 0 || target >= this.displaySections.length) return;
      const list = [...this.displaySections];
      const [item] = list.splice(index, 1);
      list.splice(target, 0, item);
      this.displaySections = list;
      this.reindexSections();
    },

    onDragStart(index) {
      this.dragIndex = index;
    },

    onDragOver(index) {
      if (this.dragIndex === null || this.dragIndex === index) return;
      const list = [...this.displaySections];
      const [item] = list.splice(this.dragIndex, 1);
      list.splice(index, 0, item);
      this.displaySections = list;
      this.dragIndex = index;
      this.reindexSections();
    },

    onDrop() {
      this.dragIndex = null;
    },

    refreshPreview() {
      this.previewKey = Date.now();
    },

    async loadVersions() {
      if (!this.templateId) return;
      this.versionsLoading = true;
      try {
        const { data } = await axios.get(`${this.apiBase}/${this.templateId}/theme/versions`);
        this.themeVersions = data.data || [];
      } catch {
        this.themeVersions = [];
      } finally {
        this.versionsLoading = false;
      }
    },

    async duplicateTheme() {
      const name = window.prompt?.('Nama tema duplikat (opsional):') ?? null;
      if (name === null) return;
      this.duplicatingTheme = true;
      try {
        const { data } = await axios.post(`${this.apiBase}/${this.templateId}/clone`, { name: name || undefined });
        window.toast?.(data.message || 'Tema diduplikasi.', 'success');
        if (data.data?.design_url) {
          window.location.href = data.data.design_url;
        }
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal menduplikasi tema.', 'error');
      } finally {
        this.duplicatingTheme = false;
      }
    },

    async bumpVersion(level) {
      if (level === 'major') {
        const ok = await window.confirmDialog?.({
          title: 'Major version (breaking)?',
          message: 'Versi major menandai perubahan breaking. Lanjutkan?',
          confirmText: 'Bump Major',
          variant: 'danger',
        });
        if (!ok) return;
      }
      this.bumpingVersion = true;
      try {
        const { data } = await axios.post(`${this.apiBase}/${this.templateId}/theme/versions/bump`, { level });
        if (data.data?.theme_version) this.themeVersion = data.data.theme_version;
        await this.loadVersions();
        window.toast?.(data.message || 'Versi dinaikkan.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal bump versi.', 'error');
      } finally {
        this.bumpingVersion = false;
      }
    },

    async previewSelectedComponent() {
      const key = this.selectedSection?.component_key;
      if (!key) return;
      this.componentPreviewLoading = true;
      try {
        const { data } = await axios.get(`${this.apiBase}/${this.templateId}/theme/components/preview`, {
          params: { component_key: key },
        });
        this.componentPreviewUrl = data.data?.preview_url || '';
        this.previewKey = Date.now();
        window.toast?.('Preview komponen dimuat.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal memuat preview komponen.', 'error');
      } finally {
        this.componentPreviewLoading = false;
      }
    },

    async restoreVersion(versionId) {
      const ok = await window.confirmDialog?.({
        title: 'Pulihkan versi tema?',
        message: 'Konfigurasi tema saat ini akan diganti dengan snapshot versi ini.',
        confirmText: 'Pulihkan',
        variant: 'danger',
      });
      if (!ok) return;

      this.restoringVersion = true;
      try {
        const { data } = await axios.post(`${this.apiBase}/${this.templateId}/theme/versions/${versionId}/restore`);
        if (data.data?.theme_version) {
          this.themeVersion = data.data.theme_version;
        }
        await this.loadTheme();
        window.toast?.(data.message || 'Versi dipulihkan.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal memulihkan versi.', 'error');
      } finally {
        this.restoringVersion = false;
      }
    },

    async save() {
      this.saving = true;
      try {
        const body = {
          theme_config: this.theme,
          form_schema: this.formSchema,
          display_sections: this.displaySections,
          layout_config: this.layoutConfig,
          design_tokens: this.designTokens,
          animation_config: this.animationConfig,
          theme_type: this.themeType,
        };
        const { data } = await axios.put(`${this.apiBase}/${this.templateId}/theme`, body);
        if (data.data?.theme_config) {
          this.theme = this.mergeTheme(data.data.theme_config);
        }
        if (data.data?.form_schema) {
          this.formSchema = data.data.form_schema;
        }
        if (data.data?.display_sections) {
          this.displaySections = this.normalizeDisplaySections(data.data.display_sections);
        }
        if (data.data?.preview_url) {
          this.previewUrl = data.data.preview_url;
        }
        if (data.data?.theme_version) {
          this.themeVersion = data.data.theme_version;
        }
        this.refreshPreview();
        window.toast?.(data.message || 'Tema disimpan.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal menyimpan tema.', 'error');
      } finally {
        this.saving = false;
      }
    },

    async uploadBackground(event) {
      const file = event.target.files?.[0];
      if (!file) return;

      this.uploading = true;
      const form = new FormData();
      form.append('background', file);

      try {
        const { data } = await axios.post(
          `${this.apiBase}/${this.templateId}/theme/background`,
          form,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        if (data.data?.cover_image_url) {
          this.coverImageUrl = data.data.cover_image_url;
          this.theme.background.cover_image = data.data.cover_image;
          this.theme.background.type = 'image';
        }
        this.refreshPreview();
        window.toast?.(data.message || 'Background diunggah.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal upload background.', 'error');
      } finally {
        this.uploading = false;
        event.target.value = '';
      }
    },
  };
}

Alpine.data('invitationThemeBuilder', createInvitationThemeBuilder);

// Backward compat alias
Alpine.data('kasirInvitationTemplateDesign', createInvitationThemeBuilder);
